import { toast } from 'sonner';
import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useForm, type UseFormRegisterReturn } from 'react-hook-form';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useChangePassword } from '../api/change-password';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface FormValues {
  confirm_password: string;
  current_password: string;
  new_password: string;
}

interface Props {
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

/**
 * The rules the API enforces, stated up front. Showing them only after a
 * rejection means typing a password twice to learn what was wanted.
 */
const RULES = '8+ characters, with an uppercase letter, a lowercase letter, a number and a symbol.';

interface FieldProps {
  autoComplete: string;
  error?: string;
  id: string;
  label: string;
  registration: UseFormRegisterReturn;
}

export function ChangePasswordDialog({ open, onOpenChange }: Props) {
  const changePassword = useChangePassword();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    reset,
    watch,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const close = () => {
    reset();
    setServerError(null);
    onOpenChange(false);
  };

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      await changePassword.mutateAsync({
        new_password: values.new_password,
        current_password: values.current_password,
      });
      toast.success('Password changed');
      close();
    } catch (err: unknown) {
      // 403 means the current password was wrong; 422 means the new one failed
      // validation. Saying which saves the user guessing at the right field.
      // The API deliberately does NOT use 401 for a wrong current password —
      // clientApi treats every 401 as an expired session and signs the user out,
      // so a typo here would have ended the session instead of showing an error.
      const status = (err as { response?: { status?: number } })?.response?.status;
      setServerError(
        status === 403
          ? 'That is not your current password.'
          : status === 422
            ? `That password does not meet the requirements — ${RULES}`
            : 'Could not change your password. Please try again.',
      );
    }
  });

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
          <DialogDescription>{RULES}</DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={onSubmit}>
          <PasswordField
            id="current_password"
            label="Current password"
            autoComplete="current-password"
            error={errors.current_password?.message}
            registration={register('current_password', { required: 'Enter your current password' })}
          />

          <PasswordField
            id="new_password"
            label="New password"
            autoComplete="new-password"
            error={errors.new_password?.message}
            registration={register('new_password', { required: 'Enter a new password' })}
          />

          <PasswordField
            id="confirm_password"
            autoComplete="new-password"
            label="Confirm new password"
            error={errors.confirm_password?.message}
            registration={register('confirm_password', {
              required: 'Confirm your new password',
              // Caught here rather than at the API, which has no idea the user
              // typed it twice.
              validate: (v) => v === watch('new_password') || 'The two passwords do not match',
            })}
          />

          {serverError && <p className="text-sm text-destructive">{serverError}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" disabled={changePassword.isPending}>
              {changePassword.isPending ? 'Changing…' : 'Change password'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A password input with a reveal toggle. Each field owns its own visibility so
 * revealing the new password doesn't also expose the current one on screen.
 */
function PasswordField({ id, label, error, registration, autoComplete }: FieldProps) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          className="pr-10"
          autoComplete={autoComplete}
          type={visible ? 'text' : 'password'}
          {...registration}
        />
        <button
          tabIndex={-1}
          type="button"
          aria-label={visible ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
          onClick={() => setVisible((v) => !v)}
        >
          {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
